import React, { useEffect, useState } from "react";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { loginEmail, loginOtpSend, loginOtpVerify } from "@/lib/api";
import { ArrowRight, Loader2, Mail, Lock, Sparkles, Eye, EyeOff, ShieldCheck } from "lucide-react";
import ScoreRing from "@/components/wishire/ScoreRing";
import WishireLogo from "@/components/wishire/WishireLogo";

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, refresh } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [mode, setMode] = useState("otp");   // otp | password — OTP is the default
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState("");
  const [info, setInfo] = useState(null);
  // OTP timers, driven by the server's expires_in / resend_after.
  const [expiresAt, setExpiresAt] = useState(null);   // ms epoch
  const [resendAt, setResendAt] = useState(null);     // ms epoch
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!otpSent) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [otpSent]);
  const secsLeft = expiresAt ? Math.max(0, Math.ceil((expiresAt - now) / 1000)) : null;
  const resendIn = resendAt ? Math.max(0, Math.ceil((resendAt - now) / 1000)) : 0;
  const expired = otpSent && secsLeft === 0;
  const mmss = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

  const routeAfterLogin = async (dataUser) => {
    const fresh = await refresh();
    const u = fresh || dataUser;
    const dest = u?.is_admin ? "/admin" : u?.role === "company" ? "/company" : u?.role === "agency" ? "/agency" : u?.role === "college" ? "/campus" : (location.state?.from || "/app");
    navigate(dest, { replace: true });
  };

  const sendOtp = async (e, { resend = false } = {}) => {
    e?.preventDefault?.();
    if (!email.trim()) { setError("Enter your email."); return; }
    if (resend && resendIn > 0) return;
    setError(null); setInfo(null); setLoading(true);
    try {
      const { data } = await loginOtpSend(email.trim().toLowerCase());
      const t = Date.now();
      setOtpSent(true);
      setOtp("");                                   // never leave a stale code in the box
      setExpiresAt(t + (data.expires_in ?? 600) * 1000);
      setResendAt(t + (data.resend_after ?? 30) * 1000);
      setNow(t);
      setInfo(data.dev_otp
        ? "Sandbox mode — code prefilled for testing."
        : resend ? "New code sent — check your inbox (and spam)." : "We've emailed you a login code.");
      if (data.dev_otp) setOtp(data.dev_otp);
    } catch (err) {
      const detail = err?.response?.data?.detail || "Could not send code.";
      // Server cooldown: honour its Retry-After so the button counts down instead of nagging.
      const retry = Number(err?.response?.headers?.["retry-after"]);
      if (err?.response?.status === 429 && retry > 0) { setOtpSent(true); setResendAt(Date.now() + retry * 1000); }
      setError(detail);
    } finally { setLoading(false); }
  };

  const verifyOtp = async (e) => {
    e?.preventDefault?.();
    if (expired) { setError("That code has expired — request a new one."); return; }
    setError(null); setLoading(true);
    try {
      const { data } = await loginOtpVerify(email.trim().toLowerCase(), otp.trim());
      await routeAfterLogin(data.user);
    } catch (err) {
      setError(err?.response?.data?.detail || "Incorrect code.");
      setOtp("");
    } finally { setLoading(false); }
  };

  // Auto-verify the moment 6 digits are in — one less tap on a phone.
  useEffect(() => {
    if (otpSent && !loading && !expired && /^\d{6}$/.test(otp)) verifyOtp();
  }, [otp]); // eslint-disable-line react-hooks/exhaustive-deps

  // After every hook: already signed in → straight to the workspace.
  if (user) {
    const dest = user.is_admin ? "/admin" : user.role === "company" ? "/company" : user.role === "agency" ? "/agency" : user.role === "college" ? "/campus" : "/app";
    return <Navigate to={dest} replace />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const { data } = await loginEmail({ email: email.trim().toLowerCase(), password });
      await routeAfterLogin(data.user);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Invalid email or password.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2 wsh-grain bg-[#FDFBF7]">
      <div className="p-10 md:p-16 flex flex-col">
        <button
          onClick={() => navigate("/")}
          className="self-start font-display text-2xl font-bold inline-flex items-baseline gap-1"
          data-testid="login-back-home"
        >
          <WishireLogo size="lg" />
        </button>

        <div className="flex-1 flex flex-col justify-center max-w-md w-full">
          <span className="tag">
            <Sparkles size={12} /> Welcome back
          </span>
          <h1 className="font-display mt-6 text-4xl md:text-5xl leading-[1.05]">
            Sign in to your{" "}
            <span className="italic text-stone-500">Wishire workspace.</span>
          </h1>

          {mode === "otp" ? (
            <form onSubmit={otpSent ? verifyOtp : sendOtp} className="mt-8 space-y-4" data-testid="login-form">
              <div>
                <label className="overline">Email</label>
                <div className="mt-2 relative">
                  <Mail size={16} className="absolute left-5 top-1/2 -translate-y-1/2 text-stone-500" />
                  <input type="email" required data-testid="login-email" value={email}
                    onChange={(e) => setEmail(e.target.value)} disabled={otpSent}
                    placeholder="you@email.com"
                    className="w-full pl-12 pr-4 py-3.5 rounded-full bg-white border border-stone-300 placeholder:text-stone-400 focus:outline-none focus:border-stone-900 text-sm disabled:bg-stone-50" />
                </div>
              </div>

              {otpSent && (
                <div>
                  <label className="overline">Login code</label>
                  <div className="mt-2 relative">
                    <ShieldCheck size={16} className="absolute left-5 top-1/2 -translate-y-1/2 text-stone-500" />
                    <input inputMode="numeric" autoComplete="one-time-code" maxLength={6} required data-testid="login-otp" value={otp}
                      onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))} autoFocus disabled={expired}
                      placeholder="6-digit code"
                      className={`w-full pl-12 pr-4 py-3.5 rounded-full bg-white border placeholder:text-stone-400 focus:outline-none focus:border-stone-900 text-sm font-mono tracking-widest ${expired ? "border-red-300 bg-red-50/40" : "border-stone-300"}`} />
                  </div>
                  {/* Expiry countdown */}
                  <p className={`mt-2 text-xs ${expired ? "text-red-700" : secsLeft !== null && secsLeft <= 60 ? "text-amber-700" : "text-stone-500"}`} data-testid="login-otp-timer">
                    {expired ? "This code has expired — request a new one below."
                      : secsLeft !== null ? `Code expires in ${mmss(secsLeft)}` : ""}
                  </p>
                  <div className="mt-2 flex items-center justify-between text-xs">
                    <button type="button" onClick={() => { setOtpSent(false); setOtp(""); setInfo(null); setError(null); setExpiresAt(null); setResendAt(null); }} className="text-stone-500 hover:text-stone-900">← Change email</button>
                    <button type="button" onClick={(e) => sendOtp(e, { resend: true })} disabled={loading || resendIn > 0} data-testid="login-resend"
                      className={`underline underline-offset-4 ${resendIn > 0 ? "text-stone-400 cursor-not-allowed no-underline" : "text-stone-700 hover:text-[#C2410C]"}`}>
                      {resendIn > 0 ? `Resend code in ${resendIn}s` : "Resend code"}
                    </button>
                  </div>
                </div>
              )}

              {info && <p className="text-emerald-700 text-sm" data-testid="login-info">{info}</p>}
              {error && <p className="text-red-700 text-sm" data-testid="login-error">{error}</p>}

              <button type="submit" disabled={loading || (otpSent && expired)} data-testid="login-submit"
                className="inline-flex items-center justify-center gap-3 w-full px-5 py-3.5 rounded-full bg-stone-900 text-stone-50 hover:bg-[#C2410C] transition-colors disabled:opacity-60">
                {loading ? <Loader2 size={16} className="animate-spin" /> : null}
                <span className="font-medium">{loading ? "Please wait…" : otpSent ? (expired ? "Code expired" : "Verify & sign in") : "Email me a login code"}</span>
                {!loading && <ArrowRight size={16} />}
              </button>

              <button type="button" onClick={() => { setMode("password"); setError(null); setInfo(null); }}
                data-testid="login-use-password" className="w-full text-center text-xs text-stone-500 hover:text-stone-900 underline underline-offset-4">
                Sign in with a password instead
              </button>
            </form>
          ) : (
            <form onSubmit={handleSubmit} className="mt-8 space-y-4" data-testid="login-form-password">
              <div>
                <label className="overline">Email</label>
                <div className="mt-2 relative">
                  <Mail size={16} className="absolute left-5 top-1/2 -translate-y-1/2 text-stone-500" />
                  <input type="email" required data-testid="login-email" value={email}
                    onChange={(e) => setEmail(e.target.value)} placeholder="you@email.com"
                    className="w-full pl-12 pr-4 py-3.5 rounded-full bg-white border border-stone-300 placeholder:text-stone-400 focus:outline-none focus:border-stone-900 text-sm" />
                </div>
              </div>
              <div>
                <label className="overline">Password</label>
                <div className="mt-2 relative">
                  <Lock size={16} className="absolute left-5 top-1/2 -translate-y-1/2 text-stone-500" />
                  <input type={showPassword ? "text" : "password"} required data-testid="login-password" value={password}
                    onChange={(e) => setPassword(e.target.value)} placeholder="Your password"
                    className="w-full pl-12 pr-12 py-3.5 rounded-full bg-white border border-stone-300 placeholder:text-stone-400 focus:outline-none focus:border-stone-900 text-sm" />
                  <button type="button" onClick={() => setShowPassword((v) => !v)} data-testid="login-password-toggle"
                    aria-label={showPassword ? "Hide password" : "Show password"} className="absolute right-4 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-700 transition-colors">
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
                <div className="mt-2 text-right">
                  <button type="button" onClick={() => navigate("/forgot-password")} data-testid="login-forgot-link"
                    className="text-xs text-stone-500 hover:text-[#C2410C] underline underline-offset-4">Forgot password?</button>
                </div>
              </div>
              {error && <p className="text-red-700 text-sm" data-testid="login-error">{error}</p>}
              <button type="submit" disabled={loading} data-testid="login-submit"
                className="inline-flex items-center justify-center gap-3 w-full px-5 py-3.5 rounded-full bg-stone-900 text-stone-50 hover:bg-[#C2410C] transition-colors disabled:opacity-60">
                {loading ? <Loader2 size={16} className="animate-spin" /> : null}
                <span className="font-medium">{loading ? "Signing in…" : "Sign in"}</span>
                {!loading && <ArrowRight size={16} />}
              </button>
              <button type="button" onClick={() => { setMode("otp"); setError(null); }}
                data-testid="login-use-otp" className="w-full text-center text-xs text-stone-500 hover:text-stone-900 underline underline-offset-4">
                Sign in with an email code instead
              </button>
            </form>
          )}

          <p className="mt-6 text-sm text-stone-600">
            Not on Wishire yet?{" "}
            <button
              type="button"
              onClick={() => navigate("/signup")}
              data-testid="login-signup-link"
              className="font-medium text-stone-900 underline underline-offset-4 hover:text-[#C2410C]"
            >
              How accounts work
            </button>
          </p>

          <button
            type="button"
            onClick={() => navigate("/")}
            className="mt-10 text-sm text-stone-600 hover:text-stone-900 self-start"
          >
            ← Back to landing
          </button>
        </div>
      </div>

      <div className="hidden md:flex items-center justify-center relative overflow-hidden bg-stone-900 text-stone-50">
        <div
          className="absolute inset-0 opacity-40"
          style={{
            background:
              "radial-gradient(700px 400px at 80% 30%, rgba(217,119,6,0.4), transparent 60%), radial-gradient(500px 400px at 10% 80%, rgba(21,128,61,0.3), transparent 60%)",
          }}
        />
        <div className="relative text-center px-10">
          <ScoreRing value={86} size={300} dark />
          <h2 className="font-display text-3xl mt-10 max-w-md">
            One number.{" "}
            <span className="italic text-amber-300">Every signal that matters.</span>
          </h2>
          <p className="mt-4 text-stone-300 max-w-sm mx-auto leading-relaxed">
            Resume quality, voice interview performance, verified skills,
            growth — your Wishire Score updates the moment you do.
          </p>
        </div>
      </div>
    </div>
  );
}

